import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";

const GuardianInfoForm = ({ selectedStudent }) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const userId = selectedStudent.userId;

  const [familyInfo, setFamilyInfo] = useState({
    fullName: "",
    primaryGuardian: "",
    relationship: "",
    cnic: "",
    phoneNumber: "",
    primaryContactMobile: "",
    email: "",
    job: "",
    employerName: "",
    countryId: "",
    stateId: "",
    cityId: "",
    currentAddress: "",
    permanentAddress: "",
    mailingAddress: "",
  });

  const [familyErrors, setFamilyErrors] = useState({
    fullName: "",
    primaryGuardian: "",
    relationship: "",
    cnic: "",
    primaryContactMobile: "",
    currentAddress: "",
    permanentAddress: "",
    mailingAddress: "",
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Format CNIC input
  const formatCnic = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 5) {
      return numbers;
    } else if (numbers.length <= 12) {
      return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    } else {
      return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(
        12,
        13
      )}`;
    }
  };

  const handleCnicChange = (e) => {
    const formattedCnic = formatCnic(e.target.value);
    setFamilyInfo({ ...familyInfo, cnic: formattedCnic });
  };

  // Format Mobile input
  const formatMobile = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 4) {
      return numbers;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 11)}`;
    }
  };

  const handleMobileChange = (e) => {
    const formattedMobile = formatMobile(e.target.value);
    setFamilyInfo({ ...familyInfo, primaryContactMobile: formattedMobile });
  };

  const fetchRelationships = async () => {
    try {
      setIsLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/relationships`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setRelationships(response || []);
    } catch (err) {
      console.error("Error fetching relationships:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setIsLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/countries`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCountries(response || []);
    } catch (err) {
      console.error("Error fetching countries:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) return;
    try {
      setIsLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/states?countryId=${countryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStates(response || []);
    } catch (err) {
      console.error("Error fetching states:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) return;
    try {
      setIsLoading(true);
      const response = await get(
        `InspStudentFamilyRegistrations/dropdowns/cities?stateId=${stateId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCities(response || []);
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudentFamilyProfile = async () => {
    if (!token || !selectedStudent.studentFirstRegistrationId) {
      console.error("Unauthorized or no student ID.");
      return;
    }

    try {
      const response = await get(
        `InspStudentFirstRegistrtion/GetStudentPersonalProfileInfo/${selectedStudent.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess && response.studentFamilyInfoDto) {
        const familyData = response.studentFamilyInfoDto;
        const relationshipName = familyData.relationship || "";
        const rel = relationships.find((r) => r.name === relationshipName);
        const relId = rel ? rel.id : "";

        setFamilyInfo({
          fullName: familyData.fullName || "",
          primaryGuardian: relationshipName,
          relationship: relId,
          cnic: familyData.cnic || "",
          phoneNumber: familyData.phoneNumber || "",
          primaryContactMobile: familyData.primaryContactMobile || "",
          email: familyData.email || "",
          job: familyData.job || "",
          employerName: familyData.employerName || "",
          countryId: familyData.countryId?.toString() || "",
          stateId: familyData.stateId?.toString() || "",
          cityId: familyData.cityId?.toString() || "",
          currentAddress: familyData.currentAddress || "",
          permanentAddress: familyData.permanentAddress || "",
          mailingAddress: familyData.mailingAddress || "",
        });
      }
    } catch (error) {
      console.error("Error fetching student family profile:", error);
    }
  };

  useEffect(() => {
    fetchRelationships();
    fetchCountries();
  }, [token]);

  useEffect(() => {
    if (relationships.length > 0 && countries.length > 0) {
      fetchStudentFamilyProfile();
    }
  }, [relationships, countries]);

  useEffect(() => {
    if (familyInfo.countryId) {
      fetchStates(familyInfo.countryId);
    }
  }, [familyInfo.countryId]);

  useEffect(() => {
    if (familyInfo.stateId) {
      fetchCities(familyInfo.stateId);
    }
  }, [familyInfo.stateId]);

  const handleSave = async () => {
    let hasError = false;
    const newErrors = { ...familyErrors };

    if (!familyInfo.fullName) {
      newErrors.fullName = "This field is required.";
      hasError = true;
    } else {
      newErrors.fullName = "";
    }

    if (!familyInfo.primaryGuardian) {
      newErrors.primaryGuardian = "This field is required.";
      hasError = true;
    } else {
      newErrors.primaryGuardian = "";
    }

    if (!familyInfo.relationship) {
      newErrors.relationship = "This field is required.";
      hasError = true;
    } else {
      newErrors.relationship = "";
    }

    if (!familyInfo.cnic || !familyInfo.cnic.match(/^\d{5}-\d{7}-\d{1}$/)) {
      newErrors.cnic = "Please enter a valid CNIC.";
      hasError = true;
    } else {
      newErrors.cnic = "";
    }

    if (
      !familyInfo.primaryContactMobile ||
      !familyInfo.primaryContactMobile.match(/^\d{4}-\d{7}$/)
    ) {
      newErrors.primaryContactMobile = "Please enter a valid mobile number.";
      hasError = true;
    } else {
      newErrors.primaryContactMobile = "";
    }

    if (!familyInfo.currentAddress) {
      newErrors.currentAddress = "This field is required.";
      hasError = true;
    } else {
      newErrors.currentAddress = "";
    }

    if (!familyInfo.permanentAddress) {
      newErrors.permanentAddress = "This field is required.";
      hasError = true;
    } else {
      newErrors.permanentAddress = "";
    }

    if (!familyInfo.mailingAddress) {
      newErrors.mailingAddress = "This field is required.";
      hasError = true;
    } else {
      newErrors.mailingAddress = "";
    }

    setFamilyErrors(newErrors);

    if (hasError) return;

    try {
      setIsLoading(true);

      // Prepare the request body according to the API specification
      const requestBody = {
        fullName: familyInfo.fullName,
        relationship: familyInfo.primaryGuardian,
        cnic: familyInfo.cnic,
        phoneNumber: familyInfo.phoneNumber,
        primaryContactMobile: familyInfo.primaryContactMobile,
        email: familyInfo.email,
        job: familyInfo.job,
        employerName: familyInfo.employerName,
        countryId: parseInt(familyInfo.countryId) || 0,
        stateId: parseInt(familyInfo.stateId) || 0,
        cityId: parseInt(familyInfo.cityId) || 0,
        mobileNumber: familyInfo.primaryContactMobile,
        primaryContactPhone: familyInfo.phoneNumber,
        permanentAddress: familyInfo.permanentAddress,
        currentAddress: familyInfo.currentAddress,
        mailingAddress: familyInfo.mailingAddress,
        centerId: centerId,
      };

      // Use the correct API endpoint
      await post(
        "InspStudentFirstRegistrtion/UpdateStudentFamilyInfo",
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Family information saved successfully.");
    } catch (err) {
      console.error("Error saving family information:", err);
      toast.error("An error occurred while saving.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-content-area d-flex flex-column h-100">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Parent Information</h5>
      </div>
      <div className="profile-scrollable-area p-1 flex-grow-1">
        <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Details
        </h5>
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Full Name <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.fullName ? "is-invalid" : ""
                  }`}
                  value={familyInfo.fullName}
                  onChange={(e) =>
                    setFamilyInfo({ ...familyInfo, fullName: e.target.value })
                  }
                  placeholder="Enter Full Name"
                />
                {familyErrors.fullName && (
                  <div className="invalid-feedback">
                    {familyErrors.fullName}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Primary Guardian <span className="text-danger fs-9">*</span>
                </label>
                <select
                  id="primaryGuardian"
                  className={`form-select custom-input ${
                    familyErrors.primaryGuardian ? "is-invalid" : ""
                  }`}
                  value={familyInfo.primaryGuardian}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      primaryGuardian: e.target.value,
                    })
                  }
                >
                  <option value="">Select Primary Guardian</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                </select>
                {familyErrors.primaryGuardian && (
                  <div className="invalid-feedback">
                    {familyErrors.primaryGuardian}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Relationship <span className="text-danger fs-9">*</span>
                </label>
                <select
                  id="relationship"
                  className={`form-select custom-input ${
                    familyErrors.relationship ? "is-invalid" : ""
                  }`}
                  value={familyInfo.relationship}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      relationship: e.target.value,
                    })
                  }
                >
                  <option value="">Select Relationship</option>
                  {relationships.map((rel) => (
                    <option key={rel.id} value={rel.id}>
                      {rel.name}
                    </option>
                  ))}
                </select>
                {familyErrors.relationship && (
                  <div className="invalid-feedback">
                    {familyErrors.relationship}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  CNIC <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="cnic"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.cnic ? "is-invalid" : ""
                  }`}
                  value={familyInfo.cnic}
                  onChange={handleCnicChange}
                  placeholder="Enter CNIC"
                  maxLength={15}
                />
                {familyErrors.cnic && (
                  <div className="invalid-feedback">{familyErrors.cnic}</div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  type="text"
                  className="form-control custom-input"
                  value={familyInfo.phoneNumber}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      phoneNumber: e.target.value,
                    })
                  }
                  placeholder="Enter Phone Number"
                />
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Primary Contact Mobile{" "}
                  <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="primaryContactMobile"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.primaryContactMobile ? "is-invalid" : ""
                  }`}
                  value={familyInfo.primaryContactMobile}
                  onChange={handleMobileChange}
                  placeholder="Enter Primary Contact Mobile"
                  maxLength={12}
                />
                {familyErrors.primaryContactMobile && (
                  <div className="invalid-feedback">
                    {familyErrors.primaryContactMobile}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Email <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-control custom-input"
                  value={familyInfo.email}
                  onChange={(e) =>
                    setFamilyInfo({ ...familyInfo, email: e.target.value })
                  }
                  placeholder="Enter Email"
                />
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">Job</label>
                <input
                  id="job"
                  type="text"
                  className="form-control custom-input"
                  value={familyInfo.job}
                  onChange={(e) =>
                    setFamilyInfo({ ...familyInfo, job: e.target.value })
                  }
                  placeholder="Enter Job"
                />
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Employer Name
                </label>
                <input
                  id="employerName"
                  type="text"
                  className="form-control custom-input"
                  value={familyInfo.employerName}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      employerName: e.target.value,
                    })
                  }
                  placeholder="Enter Employer Name"
                />
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Country <span className="text-danger fs-9">*</span>
                </label>
                <select
                  id="country"
                  className="form-select custom-input"
                  value={familyInfo.countryId}
                  onChange={(e) => {
                    setFamilyInfo({ ...familyInfo, countryId: e.target.value });
                    fetchStates(e.target.value);
                  }}
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  State <span className="text-danger fs-9">*</span>
                </label>
                <select
                  id="state"
                  className="form-select custom-input"
                  value={familyInfo.stateId}
                  onChange={(e) => {
                    setFamilyInfo({ ...familyInfo, stateId: e.target.value });
                    fetchCities(e.target.value);
                  }}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  City <span className="text-danger fs-9">*</span>
                </label>
                <select
                  id="city"
                  className="form-select custom-input"
                  value={familyInfo.cityId}
                  onChange={(e) =>
                    setFamilyInfo({ ...familyInfo, cityId: e.target.value })
                  }
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Current Address <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="currentAddress"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.currentAddress ? "is-invalid" : ""
                  }`}
                  value={familyInfo.currentAddress}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      currentAddress: e.target.value,
                    })
                  }
                  placeholder="Enter Current Address"
                />
                {familyErrors.currentAddress && (
                  <div className="invalid-feedback">
                    {familyErrors.currentAddress}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Permanent Address <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="permanentAddress"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.permanentAddress ? "is-invalid" : ""
                  }`}
                  value={familyInfo.permanentAddress}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      permanentAddress: e.target.value,
                    })
                  }
                  placeholder="Enter Permanent Address"
                />
                {familyErrors.permanentAddress && (
                  <div className="invalid-feedback">
                    {familyErrors.permanentAddress}
                  </div>
                )}
              </div>
              <div className="col-md-6 custom-input-container">
                <label className="form-label form-label-new">
                  Mailing Address <span className="text-danger fs-9">*</span>
                </label>
                <input
                  id="mailingAddress"
                  type="text"
                  className={`form-control custom-input ${
                    familyErrors.mailingAddress ? "is-invalid" : ""
                  }`}
                  value={familyInfo.mailingAddress}
                  onChange={(e) =>
                    setFamilyInfo({
                      ...familyInfo,
                      mailingAddress: e.target.value,
                    })
                  }
                  placeholder="Enter Mailing Address"
                />
                {familyErrors.mailingAddress && (
                  <div className="invalid-feedback">
                    {familyErrors.mailingAddress}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-auto pt-3">
        <div className="d-flex justify-content-end gap-3">
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuardianInfoForm;
